// src/hooks/useDarkMode.js
import { useState, useEffect } from 'react';

const systemPrefersDark = () => {
  try { return !!(window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches); }
  catch { return false; }
};

export function useDarkMode() {
  const [dark, setDark] = useState(() => {
    try {
      const saved = localStorage.getItem('tt_theme');
      if (saved === 'dark')  return true;
      if (saved === 'light') return false;
    } catch { /* ignore */ }
    // No explicit choice yet → follow the device's OS preference.
    return systemPrefersDark();
  });

  // Reflect the current theme on the DOM. We do NOT persist here — the stored
  // value only ever records an explicit user choice (see `toggle`), so an
  // untouched app keeps following the device.
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', dark ? 'dark' : 'light');
  }, [dark]);

  // While the user hasn't overridden the theme, live-follow OS changes.
  useEffect(() => {
    let mq;
    try { mq = window.matchMedia('(prefers-color-scheme: dark)'); } catch { return undefined; }
    const onChange = (e) => {
      let hasChoice = false;
      try { hasChoice = !!localStorage.getItem('tt_theme'); } catch { /* ignore */ }
      if (!hasChoice) setDark(e.matches);
    };
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);

  const toggle = () => setDark(d => {
    const next = !d;
    try { localStorage.setItem('tt_theme', next ? 'dark' : 'light'); } catch { /* ignore */ }
    return next;
  });

  return { dark, toggle };
}
